import {createContext, type ReactNode, useCallback, useContext, useMemo, useState, type VFC} from 'react'

type IssueViewerIssueEditContextState = {
  startIssueEdit: () => void
  endIssueEdit: () => void
  isIssueEditActive: boolean
}

type IssueViewerCommentsContextProps = {
  issueCommentHighlightId: string | null
  setIssueCommentHighlightId: (id: string) => void
}

const IssueViewerIssueEditContext = createContext<IssueViewerIssueEditContextState | null>(null)
const IssueViewerCommentsContext = createContext<IssueViewerCommentsContextProps | null>(null)

export const IssueViewerDataContextProvider: VFC<{children: ReactNode}> = ({children}) => {
  // ------------
  // issue comments
  // detail page sets the comment id to highlight, comment viewer uses the value to decide if it should be highlighted
  // ------------
  const [issueCommentHighlightId, setIssueCommentHighlightId] = useState<string>('')

  const commentsContextValue = useMemo(() => {
    return {
      issueCommentHighlightId,
      setIssueCommentHighlightId,
    }
  }, [issueCommentHighlightId])

  // ------------
  // issue edits
  // navigation requires knowing if an edit is in progress and to be able to end it
  // ------------
  const startIssueEdit = useCallback(() => {
    setIsIssueEditActive(true)
  }, [])
  const endIssueEdit = useCallback(() => {
    setIsIssueEditActive(false)
  }, [])
  const [isIssueEditActive, setIsIssueEditActive] = useState(false)

  const editContextValue = useMemo(() => {
    return {
      startIssueEdit,
      endIssueEdit,
      isIssueEditActive,
    }
  }, [endIssueEdit, isIssueEditActive, startIssueEdit])

  return (
    <IssueViewerIssueEditContext.Provider value={editContextValue}>
      <IssueViewerCommentsContext.Provider value={commentsContextValue}>{children}</IssueViewerCommentsContext.Provider>
    </IssueViewerIssueEditContext.Provider>
  )
}

export const useIssueViewerIssueEditContext = () => {
  const context = useContext(IssueViewerIssueEditContext)
  if (!context) {
    throw new Error('useIssueViewerIssueEditContext must be used within a IssueViewerDataContextProvider.')
  }

  return context
}

export const useIssueViewerCommentsContext = () => {
  const context = useContext(IssueViewerCommentsContext)
  if (!context) {
    throw new Error('useIssueViewerCommentsContext must be used within a IssueViewerDataContextProvider.')
  }

  return context
}

try{ IssueViewerIssueEditContext.displayName ||= 'IssueViewerIssueEditContext' } catch {}
try{ IssueViewerCommentsContext.displayName ||= 'IssueViewerCommentsContext' } catch {}
try{ IssueViewerDataContextProvider.displayName ||= 'IssueViewerDataContextProvider' } catch {}